import React, { FC, useCallback, useEffect } from 'react';

import {
	BUTTON_TYPES,
	COPY_SELECTOR_BUTTON_DAID,
	EvButton,
	EvIssueDetails
} from '@evinced-private/ui-common';

import { populatePropertiesLabel } from 'src/apps/web-sdks/helpers/WebSdkHelper';
import { CopyToClipboard } from 'src/common/components/copy-to-clipboard/CopyToClipboard';
import { mapPlatformIssueDetailsToReport } from 'src/common/components/details-block/DetailsBlockHelper';
import { IssueDetailsNewModal } from 'src/common/components/details-block/IssueDetailsNewModal';
import { TicketsSystems } from 'src/common/components/tickets-systems/TicketsSystems';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { getBaseURL } from 'src/common/helpers/UrlNormalizingHelper';
import { useIssueDetailsQuery } from 'src/common/hooks/useIssueDetailsQuery';
import { ProductType } from 'src/common/interfaces/Tenant';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';
import { Logger } from 'src/common/services/Logger';
import { usePropertiesMapQuery } from 'src/common/services/properties/hooks/usePropertiesMapQuery';
import { screenshotService } from 'src/common/services/ScreenshotService';
import { DevelopmentToggles, getSingleToggle } from 'src/common/services/TogglesService';
import { usePlatformConfigStore } from 'src/common/stores/platformConfigStore';

interface IWebSdkIssueDetailsProps {
	runId: string;
	issueId: string;
	setError?: (msg: string) => void;
	isFullPageMode?: boolean;
	// Renders the new EvIssueDetailsNew dialog (or full page) instead of the old EvIssueDetails content
	isNewModal?: boolean;
	onClose?: () => void;
}

const enableWebSdkProperties = getSingleToggle(DevelopmentToggles.ENABLE_WEB_SDK_PROPERTIES);

export const WebSdkDetailsBlock: FC<IWebSdkIssueDetailsProps> = ({
	runId,
	issueId,
	setError,
	isFullPageMode,
	isNewModal,
	onClose
}: IWebSdkIssueDetailsProps) => {
	const issueDetailsQuery = useIssueDetailsQuery({ runId, issueId, product: ProductType.WEB_SDK });

	const propertiesQuery = usePropertiesMapQuery(enableWebSdkProperties);
	const { configuration } = useConfiguration();
	const { isDefaultJiraLabelsEnabled } = usePlatformConfigStore();

	const shouldShowChatbotLink = configuration?.GLOBAL?.TOGGLES?.ENABLE_CHATBOT_LINK;
	const slackChatbotLinkConfig = configuration?.GLOBAL?.CONFIG?.SLACK_CHATBOT;
	const msTeamsChatbotLinkConfig = configuration?.GLOBAL?.CONFIG?.MS_TEAMS_CHATBOT;
	const chatbotLinkConfig = slackChatbotLinkConfig || msTeamsChatbotLinkConfig;

	useEffect(() => {
		if (issueDetailsQuery.isError) {
			const errorMessage =
				issueDetailsQuery.error instanceof Error
					? issueDetailsQuery.error.message
					: String(issueDetailsQuery.error);
			setError?.(errorMessage);
		}
	}, [issueDetailsQuery.isError, issueDetailsQuery.error, setError]);

	const issuePageUrl = RoutesHelper.getWebSdkIssueInfoPath({
		runId,
		issueId
	});

	const urlToCopy = `${getBaseURL()}${issuePageUrl}`;

	const issue = issueDetailsQuery.data;
	const {
		type,
		severity,
		elements,
		level,
		labels,
		summary,
		cause,
		howToFix,
		effect,
		knowledgeBaseLink,
		customKnowledgeBaseLinks,
		tags,
		screenshotDetails
	} = issue || {};
	const { pageUrl, pageTitle } = elements?.[0] ?? {};
	const isPropertiesLoading =
		issueDetailsQuery.isLoading || (enableWebSdkProperties && propertiesQuery.isLoading);

	const labelsToShow = isPropertiesLoading
		? labels
		: populatePropertiesLabel(labels, issue.propertyIds, propertiesQuery.data);

	const detailsIssue = {
		type,
		severity,
		elements,
		level,
		labels: labelsToShow,
		summary,
		cause,
		howToFix,
		effect,
		knowledgeBaseLink,
		customKnowledgeBaseLinks,
		tags,
		pageUrl,
		pageTitle,
		screenshotDetails
	};

	const openIssueInNewTab = useCallback((): void => {
		window.open(issuePageUrl, '_blank');
	}, [issuePageUrl]);

	const renderCopyToClipboardButton = useCallback(
		(textToCopy: string) => (
			<CopyToClipboard
				renderJustIcon
				textToCopy={textToCopy}
				dataAutomationId={COPY_SELECTOR_BUTTON_DAID}
				iconTitle="Copy Selector"
			/>
		),
		[]
	);

	if (isNewModal) {
		return (
			<IssueDetailsNewModal
				issue={issue}
				detailsIssue={detailsIssue}
				product={ProductType.WEB_SDK}
				issuePageUrl={issuePageUrl}
				isLoading={issueDetailsQuery.isLoading}
				isFullPage={isFullPageMode}
				onClose={onClose}
				copyToClipboardButton={renderCopyToClipboardButton}
			/>
		);
	}

	const topRowActions = (
		<div className="action-buttons">
			{/* In common - pass topBarActionsSection */}
			<CopyToClipboard textToCopy={urlToCopy} buttonType={BUTTON_TYPES.GHOST} />
			{!isFullPageMode && (
				<EvButton type={BUTTON_TYPES.GHOST} onClick={openIssueInNewTab}>
					Open in a New Tab
				</EvButton>
			)}
			{!issueDetailsQuery.isLoading && (
				<TicketsSystems
					issue={mapPlatformIssueDetailsToReport(
						issue,
						isDefaultJiraLabelsEnabled,
						ProductType.WEB_SDK
					)}
				/>
			)}
		</div>
	);

	return (
		<EvIssueDetails
			issue={detailsIssue}
			isLoading={issueDetailsQuery.isLoading}
			isFullPageMode={isFullPageMode}
			topRowActions={topRowActions}
			screenshotService={screenshotService}
			logger={Logger}
			copyToClipboardButton={renderCopyToClipboardButton}
			showChatbotLink={shouldShowChatbotLink}
			chatbotLinkConfig={chatbotLinkConfig}
		/>
	);
};
